import React, { useState, useEffect, useImperativeHandle, forwardRef, useCallback } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { useOnboardingDataStore } from '@/stores/onboardingDataStore';
import { upsertShootingPreferences } from '@/services/data/shootingPreferences';
import { DecorColorPicker } from './DecorColorPicker';

// Helper to convert hex to RGB string
const hexToRgb = (hex: string): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0,0,0';
  return `${Number.parseInt(result[1], 16)},${Number.parseInt(result[2], 16)},${Number.parseInt(result[3], 16)}`;
};

interface DecorSelectionStepProps {
  onComplete: () => void;
  setLoading: (loading: boolean) => void;
  onCanProceedChange?: (canProceed: boolean) => void;
  onColorChange?: (color: string) => void;
}

export interface DecorSelectionStepRef {
  handleContinue: () => void;
}

export const DecorSelectionStep = forwardRef<DecorSelectionStepRef, DecorSelectionStepProps>(
  ({ onComplete, setLoading, onCanProceedChange, onColorChange }, ref) => {
    const { userId } = useAuthStore();
    const shootingPreferences = useOnboardingDataStore((state) => state.shootingPreferences);
    const updateShootingPreferences = useOnboardingDataStore((state) => state.updateShootingPreferences);
    
    // Form state - initialize from preloaded data
    const initialColor = shootingPreferences?.decor_color || '#795AA8';
    const initialRgb = shootingPreferences?.decor_color_rgb || hexToRgb(initialColor);
    
    const [decorColor, setDecorColor] = useState<string>(initialColor);
    const [decorColorRgb, setDecorColorRgb] = useState<string>(initialRgb);

    // Sync with preloaded data when it becomes available
    useEffect(() => {
      if (shootingPreferences?.decor_color) {
        setDecorColor(shootingPreferences.decor_color);
        setDecorColorRgb(shootingPreferences.decor_color_rgb || hexToRgb(shootingPreferences.decor_color));
      }
    }, [shootingPreferences]);

    // Color always has a default value, so always allow proceeding
    useEffect(() => {
      onCanProceedChange?.(true);
    }, [onCanProceedChange]);

    // Notify parent of color changes
    useEffect(() => {
      onColorChange?.(decorColor);
    }, [decorColor, onColorChange]);

    const saveAndContinue = useCallback(async () => {
      if (!userId) return;
      
      setLoading(true);
      
      // Optimistic update in store
      updateShootingPreferences({ 
        decor_color: decorColor,
        decor_color_rgb: decorColorRgb 
      });
      
      const result = await upsertShootingPreferences(userId, {
        decor_color: decorColor,
        decor_color_rgb: decorColorRgb,
      });
      
      setLoading(false);
      
      if (result.success) {
        onComplete();
      }
    }, [userId, decorColor, decorColorRgb, setLoading, onComplete, updateShootingPreferences]);

    // Expose methods to parent via ref
    useImperativeHandle(ref, () => ({
      handleContinue: () => {
        console.log('[DecorSelection] handleContinue called');
        saveAndContinue();
      },
    }), [saveAndContinue]);

    const handleColorSelect = (color: string) => {
      setDecorColor(color);
      setDecorColorRgb(hexToRgb(color));
    };

    return (
      <DecorColorPicker
        color={decorColor}
        onColorChange={handleColorSelect}
        onContinue={saveAndContinue}
        onBack={() => {}}
      />
    );
  }
);

DecorSelectionStep.displayName = 'DecorSelectionStep';
